import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation, useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { Button } from "reactstrap";

import MaterialIcon from "shared/react_components/MaterialIcon";
import Toast from "shared/toast";
import { calculateSorting } from "shared/react_components/Comments/helpers/threadsSorting";
import SortDropdownMenu, { DEFAULT_SORTING } from "shared/react_components/Comments/SortDropdownMenu";
import Thread from "shared/react_components/Comments/Thread";
import LoaderOverlay from "shared/react_components/LoaderOverlay";

import LoadMoreThreads from "./LoadMoreThreads";
import { useOnSave, useOnDelete, useOnFollowingToggle, useUserMentionsData } from "./threadActions";
import { GetCommentThreads, GetCommentThread, SaveComment } from "calls/components/graphql/Comments.graphql";

import { addCallReferenceButton } from "calls/styles/Comments.module.scss";
import { paneTitle } from "calls/styles/CallDetails.module.scss";

const STARTING_PAGE_SIZE = 15;
const THREAD_SORTING_URL_PARAM_KEY = "sort_comments_by";

const ThreadsPanel = ({
  cuid,
  scrollToNewComment,
  onScrolledToNewComment,
  commentReferences,
  removeCommentReferences,
  onAddCallReference,
  onAudioJumpPosition
}) => {
  const history = useHistory();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const sortByFromQuery = urlSearchParams.get(THREAD_SORTING_URL_PARAM_KEY);

  const newCommentThreadRef = useRef(null);

  const [sorting, setSorting] = useState(calculateSorting(sortByFromQuery).value);
  const [threads, setThreads] = useState(null);
  const [refreshedThreadAssigned, setRefreshedThreadAssigned] = useState(true);
  const [initialCommentsLoaded, setInitialCommentsLoaded] = useState(false);
  const [pageSize, setPageSize] = useState(STARTING_PAGE_SIZE);

  const [saveComment] = useMutation(SaveComment); // For new comment threads

  const onSave = useOnSave();
  const onDelete = useOnDelete();
  const onFollowingToggle = useOnFollowingToggle();
  const userMentionsData = useUserMentionsData();

  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery(
    GetCommentThreads,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        search: { callCuid: cuid },
        sortBy: sorting,
        first: pageSize
      },
      pollInterval: 15000
    }
  );

  // skip so initial query is never called with no variables provided
  const { refetch: threadRefetch } = useQuery(GetCommentThread, { skip: true });

  useEffect(() => {
    if (scrollToNewComment && newCommentThreadRef.current) {
      newCommentThreadRef.current.scrollIntoView({ block: "center" });
      onScrolledToNewComment && onScrolledToNewComment();
    }
  }, [scrollToNewComment, onScrolledToNewComment, newCommentThreadRef]);

  useEffect(() => {
    if (!loading && refreshedThreadAssigned) {
      setThreads(data && data.commentThreads && data.commentThreads.nodes);
    }
  }, [data, loading, networkStatus, refreshedThreadAssigned]);

  useEffect(() => {
    if (!loading && !initialCommentsLoaded) {
      if (error) {
        Toast.error({
          title: "Couldn't load Comments",
          message: "An error occurred while trying to load comments. Please try again."
        });
      }
      setInitialCommentsLoaded(true);
    }
  }, [loading, networkStatus, initialCommentsLoaded, error]);

  const onSortPick = ({ value }) => {
    setSorting(value);
    setPageSize(STARTING_PAGE_SIZE);
    urlSearchParams.set(THREAD_SORTING_URL_PARAM_KEY, value);
    history.push({ pathname: location.pathname, search: `?${urlSearchParams.toString()}` });
  };

  const createComment = async ({ richText, mentions, uiLocationIndex, transcriptRef, callRecordingRef }) => {
    await saveComment({
      variables: {
        attributes: { richText, mentions, uiLocationIndex, callRecordingRef, transcriptRef },
        search: { callCuid: cuid }
      }
    });
    removeCommentReferences();
    // Update sorting to ensure the newly created comment thread is shown at the top of the page.
    onSortPick({ value: DEFAULT_SORTING.value });
    return refetch();
  };

  const matchingThreadIndex = (threadId) => threads && threads.findIndex(thread => thread.hashid === threadId);

  const updatedThreadRefetch = async (threadId) => {
    setRefreshedThreadAssigned(false);
    const { data } = await threadRefetch({ hashid: threadId });
    const index = matchingThreadIndex(data.commentThread.hashid);
    const updatedThreads = threads;
    updatedThreads[index] = data.commentThread;
    setRefreshedThreadAssigned(true);
    setThreads(updatedThreads);
    return data;
  };

  const updateResults = (previousResult, { fetchMoreResult }) => ({
    commentThreads: {
      __typename: previousResult.commentThreads.__typename,
      nodes: [...previousResult.commentThreads.nodes, ...fetchMoreResult.commentThreads.nodes],
      pageInfo: fetchMoreResult.commentThreads.pageInfo
    }
  });

  const loadMore = async () => {
    try {
      await fetchMore({
        variables: { cursor: data.commentThreads.pageInfo.endCursor },
        updateQuery: updateResults
      });
      setPageSize(pageSize + STARTING_PAGE_SIZE);
    } catch (error) {
      Toast.error({
        title: "Error loading more comments",
        message: "An error occurred while trying to load more comments. Please try again."
      });
    }
  };

  const addCallReferenceButtonHeader = () => {
    if (onAddCallReference && Object.keys(commentReferences).length === 0) {
      return (
        <Button data-test-id="add-call-reference" className={addCallReferenceButton} outline color="link" onClick={onAddCallReference}>
          <MaterialIcon className="pr-1" name="add"/>Add Call Reference
        </Button>
      );
    }
  };

  const displayThread = thread => {
    if (thread.comments.length > 0) {
      const onSaveForThread = async ({ hashid: commentId, richText, mentions }) => {
        await onSave({ threadId: thread.hashid, commentId, richText, mentions });
        await updatedThreadRefetch(thread.hashid);
      };

      const onThreadFollowingToggle = async () => {
        if (await onFollowingToggle({ threadId: thread.hashid, following: thread.following })) {
          await updatedThreadRefetch(thread.hashid);
        }
      };

      const onDeleteForThread = async (hashid) => {
        await onDelete({ threadId: thread.hashid, commentId: hashid });
        await updatedThreadRefetch(thread.hashid);
      };

      return (
        <Thread
          key={thread.hashid}
          threadHashid={thread.hashid}
          following={thread.following}
          comments={thread.comments}
          onFollowingToggle={onThreadFollowingToggle}
          onDelete={onDeleteForThread}
          onSave={onSaveForThread}
          onAudioJump={onAudioJumpPosition}
          userMentionsData={userMentionsData}
        />
      );
    }
  };

  const hasNextPage = data && data.commentThreads && data.commentThreads.pageInfo && data.commentThreads.pageInfo.hasNextPage;

  return (
    <LoaderOverlay loading={!initialCommentsLoaded}>
      <div className="d-flex mb-3">
        <span className={paneTitle}> Comments </span>
        <SortDropdownMenu value={sorting} isLoading={!initialCommentsLoaded} onChange={onSortPick} />
      </div>

      <div ref={newCommentThreadRef}>
        <Thread
          comments={[]}
          onSave={createComment}
          userMentionsData={userMentionsData}
          onAudioJump={onAudioJumpPosition}
          newCommentReferences={commentReferences}
          onRemoveNewCommentReferences={removeCommentReferences}
          focusNewComment={scrollToNewComment}
          header={addCallReferenceButtonHeader()}
        />
      </div>
      {threads && threads.map((thread) => displayThread(thread))}
      <LoadMoreThreads networkStatus={networkStatus} hasNextPage={hasNextPage} loadMore={loadMore} />
    </LoaderOverlay>
  );
};

ThreadsPanel.propTypes = {
  cuid: PropTypes.string.isRequired,
  scrollToNewComment: PropTypes.bool,
  onScrolledToNewComment: PropTypes.func,
  commentReferences: PropTypes.object,
  removeCommentReferences: PropTypes.func,
  onAddCallReference: PropTypes.func,
  onAudioJumpPosition: PropTypes.func
};

ThreadsPanel.defaultProps = {
  commentReferences: {},
  removeCommentReferences: () => {}
};

export default ThreadsPanel;
